import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type Material = Tables<"materials">;
export type Application = Tables<"applications">;

export const useMaterials = () => {
  return useQuery({
    queryKey: ["materials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Material[];
    },
  });
};

export const useMaterial = (id: string | undefined) => {
  return useQuery({
    queryKey: ["material", id],
    queryFn: async () => {
      if (!id) throw new Error("No material ID provided");
      
      const { data, error } = await supabase
        .from("materials")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;
      return data as Material | null;
    },
    enabled: !!id,
  });
};

export const useApplications = () => {
  return useQuery({
    queryKey: ["applications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Application[];
    },
  });
};

export const useApplication = (id: string | undefined) => {
  return useQuery({
    queryKey: ["application", id],
    queryFn: async () => {
      if (!id) throw new Error("No application ID provided");
      
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;
      return data as Application | null;
    },
    enabled: !!id,
  });
};

export const useApplicationMaterials = (applicationId: string | undefined) => {
  return useQuery({
    queryKey: ["application-materials", applicationId],
    queryFn: async () => {
      if (!applicationId) throw new Error("No application ID provided");

      const { data, error } = await supabase
        .from("application_materials")
        .select(`
          material_id,
          materials (*)
        `)
        .eq("application_id", applicationId);

      if (error) throw error;
      return data;
    },
    enabled: !!applicationId,
  });
};

export const useMaterialApplications = (materialId: string | undefined) => {
  return useQuery({
    queryKey: ["material-applications", materialId],
    queryFn: async () => {
      if (!materialId) throw new Error("No material ID provided");

      const { data, error } = await supabase
        .from("application_materials")
        .select(`
          application_id,
          applications (*)
        `)
        .eq("material_id", materialId);

      if (error) throw error;
      return data;
    },
    enabled: !!materialId,
  });
};
